import {isCopilotPath, isGitHubHomePath} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useEffect, useState} from 'react'

import type {ImplicitContextPage} from '../contexts/ImplicitContextPageContext'

/**
 * Top-level path segments that are never a repository owner. Used only as a
 * fallback when the repository meta tag is absent (e.g. octolytics disabled in
 * some environments); the server still authoritatively ignores non-entity pages
 * via route recognition, so this list only needs to avoid obviously-wrong fetches.
 */
const RESERVED_OWNER_SEGMENTS = new Set([
  'about',
  'account',
  'advisories',
  'apps',
  'businesses',
  'codespaces',
  'collections',
  'contact',
  'copilot',
  'dashboard',
  'discussions',
  'enterprises',
  'events',
  'explore',
  'features',
  'github-copilot',
  'issues',
  'join',
  'login',
  'logout',
  'marketplace',
  'new',
  'notifications',
  'organizations',
  'orgs',
  'pricing',
  'projects',
  'pulls',
  'search',
  'security',
  'sessions',
  'settings',
  'signup',
  'sponsors',
  'stars',
  'topics',
  'trending',
  'users',
  'watching',
])

/**
 * Reads the repository owner/name for the current page. Prefers the
 * `octolytics-dimension-repository_nwo` meta tag, which the server renders on
 * every repository page (and omits everywhere else), and falls back to parsing
 * the first two path segments when the meta tag is unavailable. Returns the
 * `owner/repo` nwo string, or `null` when the page is not a repository page.
 */
function readCurrentRepoNwo(): string | null {
  const metaNwo = document.querySelector('meta[name="octolytics-dimension-repository_nwo"]')?.getAttribute('content')
  if (parseRepoNwo(metaNwo)) return metaNwo ?? null

  const [, owner, repo] = window.location.pathname.split('/')
  if (owner && repo && !RESERVED_OWNER_SEGMENTS.has(owner.toLowerCase())) {
    return `${owner}/${repo}`
  }

  return null
}

function parseRepoNwo(nwo: string | null | undefined): {owner: string; repo: string} | null {
  if (!nwo) return null
  const [owner, repo] = nwo.split('/')
  if (!owner || !repo) return null
  return {owner, repo}
}

/**
 * Pure derivation of the {@link ImplicitContextPage} from a repository nwo, the
 * current location parts, and the page title. Repository pages carry
 * `owner`/`repo`; other pages carry empty `owner`/`repo` so the resolver can
 * synthesize a `github_url` fallback downstream. Returns `null` only for pages
 * with no attachable context at all — the copilot chat routes and the signed-in
 * home/dashboard (general-intent pages, mirroring copilot-chat's history
 * watcher). Extracted so it can be unit-tested without a DOM.
 */
export function resolveImplicitContextPage(
  nwo: string | null | undefined,
  location: {pathname: string; search: string; hash: string},
  title?: string,
): ImplicitContextPage | null {
  if (isCopilotPath(location.pathname) || isGitHubHomePath(location.pathname)) return null

  const url = `${location.pathname}${location.search}${location.hash}`
  const parsed = parseRepoNwo(nwo)
  if (!parsed) return {owner: '', repo: '', url, title}
  return {owner: parsed.owner, repo: parsed.repo, url, title}
}

/** Builds the {@link ImplicitContextPage} for the host page, or `null` when there is no attachable context. */
export function readCurrentImplicitContextPage(): ImplicitContextPage | null {
  if (typeof window === 'undefined') return null

  const nwo = readCurrentRepoNwo()
  const {pathname, search, hash} = window.location
  const title = typeof document !== 'undefined' ? document.title : undefined
  return resolveImplicitContextPage(nwo, {pathname, search, hash}, title)
}

function samePage(a: ImplicitContextPage | null, b: ImplicitContextPage | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.owner === b.owner && a.repo === b.repo && a.url === b.url && a.title === b.title
}

export interface CurrentImplicitContextPage {
  /**
   * The reactive current page for display (chip rendering). Updated immediately
   * on structural navigation and debounced for bursty line-selection changes.
   */
  page: ImplicitContextPage | null
  /**
   * Reads the freshest page directly from the DOM. Call this at submit time so
   * the resolved context matches the actual current location even when the
   * reactive `page` is momentarily stale — e.g. during the line-selection
   * debounce window, or after a router-driven same-route hash navigation that
   * fires no `hashchange`/`statechange` event (Code View symbol-panel jumps).
   */
  getLatestPage: () => ImplicitContextPage | null
}

/**
 * Resolves the host page the embedded chat is mounted on into an
 * {@link ImplicitContextPage}. The embedded partial is a globally-mounted shared
 * fragment, so — unlike the per-page immersive chat — it can't rely on
 * server-seeded references; it reads the current page from the DOM instead and
 * re-evaluates on navigation so implicit context follows the user around.
 */
export function useCurrentImplicitContextPage(): CurrentImplicitContextPage {
  const [page, setPage] = useState<ImplicitContextPage | null>(readCurrentImplicitContextPage)

  const updateNow = useCallback(() => {
    setPage(prev => {
      const next = readCurrentImplicitContextPage()
      return samePage(prev, next) ? prev : next
    })
  }, [])

  // Code View emits a burst of hash/statechange events while the user moves the
  // line-range selection (keyboard/drag), and each new URL changes the TanStack
  // query key, kicking off another implicit-context request. Debounce only these
  // bursty line-selection re-reads (matching copilot-chat's 500 ms history-fetch
  // debounce) so rapid range changes coalesce into a single fetch. Structural
  // navigations update immediately below so a quick submit right after navigating
  // never resolves against the previous page.
  const debouncedUpdate = useDebounce(updateNow, 500, {onChangeBehavior: 'cancel'})

  useEffect(() => {
    // Structural navigations (soft nav, back/forward) update immediately: the
    // context and submit-time resolution must point at the new page right away.
    document.addEventListener(SOFT_NAV_STATE.SUCCESS, updateNow)
    window.addEventListener('popstate', updateNow)
    // Code View updates the selected line range via `window.location.hash`
    // (hashchange) or `@github-ui/history` (statechange) — neither fires a soft-
    // nav success or popstate — so debounce both to keep the snippet range
    // current without a fetch per intermediate range.
    window.addEventListener('hashchange', debouncedUpdate)
    window.addEventListener('statechange', debouncedUpdate)
    return () => {
      debouncedUpdate.cancel()
      document.removeEventListener(SOFT_NAV_STATE.SUCCESS, updateNow)
      window.removeEventListener('popstate', updateNow)
      window.removeEventListener('hashchange', debouncedUpdate)
      window.removeEventListener('statechange', debouncedUpdate)
    }
  }, [updateNow, debouncedUpdate])

  return {page, getLatestPage: readCurrentImplicitContextPage}
}
